/**
 * Text Analyzer Service
 * Implements text analysis and transformation operations
 * Supports counting, case conversion, and whitespace operations
 */

import { EncodingOperation } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';

export type CaseType = 'upper' | 'lower' | 'title' | 'camel' | 'snake' | 'kebab';

export interface TextStats {
  characters: number;
  charactersNoSpaces: number;
  words: number;
  lines: number;
  paragraphs: number;
  sentences: number;
}

export class TextAnalyzerService {
  /**
   * Analyze text and return statistics
   */
  static async analyze(text: string): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(text,
        CommonValidator.getValidationRules('text-analyzer' as any)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, 'text-analyzer' as any, validation.suggestion);
      }

      // Calculate statistics
      const stats = this.calculateStats(text);

      return {
        id: this.generateOperationId(),
        toolType: 'text-analyzer' as any,
        operationType: 'analyze',
        inputData: text,
        outputData: stats,
        timestamp: new Date(),
        status: 'success',
        metadata: stats
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to analyze text',
        'text-analyzer' as any,
        'Please check your input and try again'
      );
    }
  }

  /**
   * Convert text case
   */
  static async convertCase(text: string, caseType: CaseType): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(text,
        CommonValidator.getValidationRules('text-analyzer' as any)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, 'text-analyzer' as any, validation.suggestion);
      }

      // Convert case
      const convertedText = this.performCaseConversion(text, caseType);

      return {
        id: this.generateOperationId(),
        toolType: 'text-analyzer' as any,
        operationType: 'convert-case',
        inputData: text,
        outputData: convertedText,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          caseType,
          originalLength: text.length,
          convertedLength: convertedText.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to convert text case',
        'text-analyzer' as any,
        'Please check your input and try again'
      );
    }
  }

  /**
   * Perform whitespace operations
   */
  static async normalizeWhitespace(text: string): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(text,
        CommonValidator.getValidationRules('text-analyzer' as any)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, 'text-analyzer' as any, validation.suggestion);
      }

      // Normalize whitespace
      const normalized = this.performWhitespaceNormalization(text);

      return {
        id: this.generateOperationId(),
        toolType: 'text-analyzer' as any,
        operationType: 'normalize-whitespace',
        inputData: text,
        outputData: normalized,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          originalLength: text.length,
          normalizedLength: normalized.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to normalize whitespace',
        'text-analyzer' as any,
        'Please check your input and try again'
      );
    }
  }

  /**
   * Calculate text statistics
   */
  private static calculateStats(text: string): TextStats {
    const characters = text.length;
    const charactersNoSpaces = text.replace(/\s/g, '').length;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const lines = text.split(/\n/).length;
    const paragraphs = text.trim() ? text.split(/\n\s*\n/).filter(p => p.trim()).length : 0;
    const sentences = text.trim() ? text.split(/[.!?]+/).filter(s => s.trim()).length : 0;

    return {
      characters,
      charactersNoSpaces,
      words,
      lines,
      paragraphs,
      sentences
    };
  }

  /**
   * Perform case conversion
   */
  private static performCaseConversion(text: string, caseType: CaseType): string {
    switch (caseType) {
      case 'upper':
        return text.toUpperCase();

      case 'lower':
        return text.toLowerCase();

      case 'title':
        return text.replace(/\w\S*/g, (word) =>
          word.charAt(0).toUpperCase() + word.substr(1).toLowerCase()
        );

      case 'camel':
        return text
          .toLowerCase()
          .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase());

      case 'snake':
        return text
          .replace(/\W+/g, ' ')
          .split(/ |\B(?=[A-Z])/)
          .map(word => word.toLowerCase())
          .join('_');

      case 'kebab':
        return text
          .replace(/\W+/g, ' ')
          .split(/ |\B(?=[A-Z])/)
          .map(word => word.toLowerCase())
          .join('-');

      default:
        return text;
    }
  }

  /**
   * Perform whitespace normalization
   */
  private static performWhitespaceNormalization(text: string): string {
    return text
      .replace(/[ \t]+/g, ' ')  // Replace multiple spaces/tabs with single space
      .replace(/\n\s*\n/g, '\n\n')  // Normalize multiple blank lines to double newline
      .trim();  // Remove leading/trailing whitespace
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `text-analyzer-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get case type info for display
   */
  static getCaseTypeInfo(caseType: CaseType): { name: string; description: string } {
    switch (caseType) {
      case 'upper':
        return { name: 'UPPERCASE', description: 'Convert all characters to uppercase' };
      case 'lower':
        return { name: 'lowercase', description: 'Convert all characters to lowercase' };
      case 'title':
        return { name: 'Title Case', description: 'Capitalize first letter of each word' };
      case 'camel':
        return { name: 'camelCase', description: 'Convert to camelCase format' };
      case 'snake':
        return { name: 'snake_case', description: 'Convert to snake_case format' };
      case 'kebab':
        return { name: 'kebab-case', description: 'Convert to kebab-case format' };
      default:
        return { name: 'UPPERCASE', description: 'Convert to uppercase' };
    }
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
